import { Box, Chip, Grid, LinearProgress, Paper, styled, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { auth } from "../firebase/firebase";
import { argosApiEntryPoint } from "../GlobalConfig";
import Chart from "react-google-charts";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import InfoIcon from '@mui/icons-material/Info';
import { Item } from "./CommonComponents";

// Interface for retraction data by month
interface RetractionByMonth {
    year: number;
    month: number;
    retractions: number;
}

// Interface for journal retraction data
interface JournalRetraction {
    Journal: string;
    journal_retractions_for_period: number;
    journal_retractions_for_previous_period: number;
}

// Interface for publisher retraction data
interface PublisherRetraction {
    Publisher: string;
    publisher_retractions_for_period: number;
    publisher_retractions_for_previous_period: number;
}

// Main interface that contains all the deserialized response data
interface RetractionData {
    last_12_month_retractions_by_month: RetractionByMonth[];
    last_12_month_retractions: number;
    last_6_month_retractions: number;
    last_3_month_retractions: number;
    retractions_reported_by_rw: number;
    last_12_month_retractions_reported_by_rw: number;
    last_6_month_retractions_reported_by_rw: number;
    last_3_month_retractions_reported_by_rw: number;
    total_high_risk: number;
    total_medium_risk: number;
    updated_time: string;
    most_retracted_journals_3_months: JournalRetraction[];
    most_retracted_journals_6_months: JournalRetraction[];
    most_retracted_journals_12_months: JournalRetraction[];
    most_retracted_publishers_3_months: PublisherRetraction[];
    most_retracted_publishers_6_months: PublisherRetraction[];
    most_retracted_publishers_12_months: PublisherRetraction[];
    biggest_movers_journals: JournalRetraction[];
    biggest_movers_publishers: PublisherRetraction[];
}


function RetractionControlCenter() {

    const [dashboardData, setDashboardData] = useState<RetractionData>()
    const [retractionsPerMonth, setRetractionsPerMonth] = useState<any[][]>()
    const [threeMonthRetractionsPerJournal, setThreeMonthRetractionsPerJournal] = useState<any[][]>()
    const [sixMonthRetractionsPerJournal, setSixMonthRetractionsPerJournal] = useState<any[][]>()
    const [twelveMonthRetractionsPerJournal, setTwelveMonthRetractionsPerJournal] = useState<any[][]>()
    const [threeMonthsRetractionsPerPublisher, setThreeMonthsRetractionsPerPublisher] = useState<any[][]>()
    const [sixMonthsRetractionsPerPublisher, setSixMonthsRetractionsPerPublisher] = useState<any[][]>()
    const [twelveMonthsRetractionsPerPublisher, setTwelveMonthsRetractionsPerPublisher] = useState<any[][]>()
    const [isLoading, setIsLoading] = useState(false)
    const [hasInitialData, setHasInitialData] = useState(false)
    const [selectedTimeframe, setSelectedTimeframe] = useState(3)

    const biggest_movers_explanation =
        "Ranking representing the biggest proportional changes in retractions comparing the current period with the inmediately previous period. Designed to identify areas where an increase or decrease of activity has ocurred. The Change value is calculated by substracting the number of retractions for the previous period from the number of retractions for the current period."

    const retractionsPerMonthColumnHeaders: any[][] = [
        [
            { type: "date", label: "Month" },
            { type: "number", label: "Retractions" }
        ]
    ]

    const retractionsPerJournalColumnHeaders: any[][] = [
        [
            { type: "string", label: "Journal" },
            { type: "number", label: "Retractions" }
        ]
    ]


    const retractionsPerPublisherColumnHeaders: any[][] = [
        [
            { type: "string", label: "Publisher" },
            { type: "number", label: "Retractions" }
        ]
    ]

    interface DateTimePair {
        date: Date;
        value: number;
    }

    function aggregateValues(pairs: DateTimePair[]): number {
        return pairs.reduce((accumulator, currentPair) => accumulator + currentPair.value, 0);
    }

    function aggregateFutureDates(pairs: DateTimePair[]): DateTimePair[] {
        const currentMonth = new Date();
        currentMonth.setDate(1); // Set to the first day of the current month (ignoring the time)
        currentMonth.setHours(0, 0, 0, 0); // Set to midnight to ignore the time component

        // Filter out the future month entries and return the aggregated list
        const futureMonths = pairs.filter(pair => pair.date.getTime() > currentMonth.getTime());
        const pastAndCurrentMonth = pairs.filter(pair => pair.date.getTime() <= currentMonth.getTime());
        const adjustedMonthsAggregatingFutureIntoCurrentMonth = [...pastAndCurrentMonth.slice(0, pastAndCurrentMonth.length - 1),
        {
            date: pastAndCurrentMonth[pastAndCurrentMonth.length - 1].date,
            value: pastAndCurrentMonth[pastAndCurrentMonth.length - 1].value + aggregateValues(futureMonths)
        }
        ];
        return adjustedMonthsAggregatingFutureIntoCurrentMonth;
    }


    function prepareJournalData(data: JournalRetraction[]): any[][] {
        let shapedData: any[][] = data
            .map(({ Journal, journal_retractions_for_period, journal_retractions_for_previous_period }) =>
                [Journal, journal_retractions_for_period])
        return retractionsPerJournalColumnHeaders.concat(shapedData)
    }

    function preparePublisherData(data: PublisherRetraction[]): any[][] {
        let shapedData: any[][] = data
            .map(({ Publisher, publisher_retractions_for_period, publisher_retractions_for_previous_period }) =>
                [Publisher, publisher_retractions_for_period])
        return retractionsPerPublisherColumnHeaders.concat(shapedData)
    }



    useEffect(() => {
        async function fetchData() {
            setIsLoading(true)
            let authToken = await auth.currentUser?.getIdToken(true)
            fetch(argosApiEntryPoint + '/simple-dashboard?name=retraction-control-center'
                , {
                    method: 'GET',
                    headers: {
                        'content-type': 'application/json;charset=UTF-8',
                        'Authorization': 'Bearer ' + authToken
                    }
                })
                .then((response) => response.json())
                .then((data: RetractionData) => {
                    setDashboardData(data)
                    let tmpRpm: DateTimePair[] = data.last_12_month_retractions_by_month
                        .flatMap(({ year, month, retractions }) => [{ date: new Date(year, month - 1), value: retractions }])
                    let aggregatedRpm: DateTimePair[] = aggregateFutureDates(tmpRpm)
                    let rpm: any[][] = aggregatedRpm.map((value) => [value.date, value.value])
                    setRetractionsPerMonth(retractionsPerMonthColumnHeaders.concat(rpm))
                    setThreeMonthRetractionsPerJournal(prepareJournalData(data.most_retracted_journals_3_months))
                    setSixMonthRetractionsPerJournal(prepareJournalData(data.most_retracted_journals_6_months))
                    setTwelveMonthRetractionsPerJournal(prepareJournalData(data.most_retracted_journals_12_months))
                    setThreeMonthsRetractionsPerPublisher(preparePublisherData(data.most_retracted_publishers_3_months))
                    setSixMonthsRetractionsPerPublisher(preparePublisherData(data.most_retracted_publishers_6_months))
                    setTwelveMonthsRetractionsPerPublisher(preparePublisherData(data.most_retracted_publishers_12_months))
                    setIsLoading(false)
                    setHasInitialData(true)
                })
                ;
        }
        fetchData()
    }, []);


    const retractionsPerMonthOptions = {
        legend: { position: 'none' },
        height: 800,
        hAxis: { format: 'MMM yy' }
    };

    var paddingHeight = 40;
    var rowHeight = (threeMonthRetractionsPerJournal?.length == undefined ? 0 : threeMonthRetractionsPerJournal?.length) * 50;
    var chartHeight = rowHeight + paddingHeight;

    const retractionsPerJournalOptions = {
        legend: { position: 'none' },
        height: chartHeight,
        chartArea: {
            height: rowHeight,
        },
    };

    const retractionsPerPublisherOptions = {
        legend: { position: 'none' },
        height: chartHeight,
        chartArea: {
            height: rowHeight,
        },
    };

    const biggestMoversJournalsColumns: GridColDef<(JournalRetraction)>[] = [
        { field: 'Journal', headerName: 'Journal', flex: 0.8 },
        { field: 'journal_retractions_for_period', headerName: 'Retractions', flex: 0.3 },
        {
            field: 'journal_retractions_for_previous_period', headerName: 'Change', flex: 0.2,
            renderCell: (e) => {
                return <Typography variant="caption">
                    {(e.row.journal_retractions_for_period - e.row.journal_retractions_for_previous_period > 0) &&
                        "+"
                    }
                    {e.row.journal_retractions_for_period - e.row.journal_retractions_for_previous_period}
                </Typography>
            }
        }
    ];

    const biggestMoversPublisherColumns: GridColDef<(PublisherRetraction)>[] = [
        { field: 'Publisher', headerName: 'Publisher', flex: 0.8 },
        { field: 'publisher_retractions_for_period', headerName: 'Retractions', flex: 0.3 },
        {
            field: 'publisher_retractions_for_previous_period', headerName: 'Change', flex: 0.2,
            renderCell: (e) => {
                return <Typography variant="caption">
                    {(e.row.publisher_retractions_for_period - e.row.publisher_retractions_for_previous_period > 0) &&
                        "+"
                    }
                    {e.row.publisher_retractions_for_period - e.row.publisher_retractions_for_previous_period}
                </Typography>
            }
        }
    ];

    function getRowIdForJournal(row: JournalRetraction) {
        return row.Journal;
    }


    function getRowIdForPublisher(row: PublisherRetraction) {
        return row.Publisher;
    }

    function formatUpdatedTime(dateString: string): string {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
        const day = date.getDate().toString().padStart(2, '0');
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');

        return `${year}-${month}-${day} ${hours}:${minutes}`;
    }

    function establish3MonthTimeframe() {
        setSelectedTimeframe(3)
    }

    function establish6MonthTimeframe() {
        setSelectedTimeframe(6)
    }

    function establish12MonthTimeframe() {
        setSelectedTimeframe(12)
    }

    function retractionsPerJournalData() {
        switch (selectedTimeframe) {
            case 6: {
                return sixMonthRetractionsPerJournal
            }
            case 12: {
                return twelveMonthRetractionsPerJournal
            }
            default:
                return threeMonthRetractionsPerJournal
        }
    }


    function retractionsPerPublisherData() {
        switch (selectedTimeframe) {
            case 6: {
                return sixMonthsRetractionsPerPublisher
            }
            case 12: {
                return twelveMonthsRetractionsPerPublisher
            }
            default:
                return threeMonthsRetractionsPerPublisher
        }
    }

    return <Box>
        <Typography gutterBottom variant="h3" component="div" sx={{ color: '#003366', fontWeight: 'bold'}}>
            Retraction Control Center
        </Typography>        
        {isLoading &&
            <LinearProgress key={"latest_retractions" + Math.random()} />}
        {!isLoading &&
            <>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <Item elevation={4}>
                            <Typography variant="caption" gutterBottom>12 month retractions</Typography>
                            <Typography variant="h2" gutterBottom>{dashboardData?.last_12_month_retractions}</Typography>
                        </Item>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Item elevation={4}>
                            <Typography variant="caption" gutterBottom>6 month retractions</Typography>
                            <Typography variant="h2" gutterBottom>{dashboardData?.last_6_month_retractions}</Typography>
                        </Item>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Item elevation={4}>
                            <Typography variant="caption" gutterBottom>3 month retractions</Typography>
                            <Typography variant="h2" gutterBottom>{dashboardData?.last_3_month_retractions}</Typography>
                        </Item>
                    </Grid>
                </Grid>
                <Grid container spacing={2} mt={1}>
                    <Grid item xs={12} sm={6} md={4}>
                        <Item elevation={4}>
                            <Typography variant="caption" gutterBottom>Total High Risk articles in circulation</Typography>
                            <Typography variant="h2" gutterBottom>{dashboardData?.total_high_risk}</Typography>
                        </Item>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Item elevation={4}>
                            <Typography variant="caption" gutterBottom>Total Medium Risk articles in circulation</Typography>
                            <Typography variant="h2" gutterBottom>{dashboardData?.total_medium_risk}</Typography>
                        </Item>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Item elevation={4}>
                            <Typography variant="caption" gutterBottom>Dashboard updated time</Typography>
                            <Typography variant="body1" gutterBottom>{dashboardData && formatUpdatedTime(dashboardData?.updated_time)} UTC</Typography>
                            <Typography variant="caption" gutterBottom component="div">Using rolling periods, example: 3 months from  21th December 2024 to 21th March 2025</Typography>

                        </Item>
                    </Grid>
                </Grid>
                <Grid container spacing={2} mt={1}>
                    <Grid item xs={12}>
                        <Item elevation={4}>
                            <Typography variant="caption" gutterBottom>Retractions per month (Last 12 Months)</Typography>
                            <Tooltip key="retractions_per_month_tooltip" placement="top" title="Retractions that have been published with a retraction date in the future have been aggregated into the current month">
                                <InfoIcon color="primary" fontSize="small" style={{ verticalAlign: 'top' }} />
                            </Tooltip>
                            {hasInitialData &&
                                <Chart
                                    chartType="ColumnChart"
                                    data={retractionsPerMonth}
                                    options={retractionsPerMonthOptions}
                                />
                            }
                        </Item>
                    </Grid>
                </Grid>
                <Item sx={{ mt: 2 }}>
                    <Grid container spacing={2} mt={1}>
                        <Grid item xs={12}>
                            <Grid container spacing={2} mt={1}>
                                <Grid item xs={12}>
                                    <Box marginTop={2}>
                                        <Typography variant="caption">
                                            Timeframe:
                                            <Chip label="Last 3 Months" onClick={establish3MonthTimeframe}
                                                {
                                                ...(selectedTimeframe === 3) ? { variant: "filled" } : { variant: "outlined" }
                                                } color="primary" sx={{ marginRight: 2 }} />
                                            <Chip label="Last 6 Months" onClick={establish6MonthTimeframe}
                                                {
                                                ...(selectedTimeframe === 6) ? { variant: "filled" } : { variant: "outlined" }
                                                } color="primary" sx={{ marginRight: 2 }} />
                                            <Chip label="Last 12 Months" onClick={establish12MonthTimeframe}
                                                {
                                                ...(selectedTimeframe === 12) ? { variant: "filled" } : { variant: "outlined" }
                                                } color="primary" />
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Item elevation={4}>
                                        <Typography variant="caption" gutterBottom>Journals with most retractions (Last {selectedTimeframe} Months)</Typography>
                                        {hasInitialData &&
                                            <Chart
                                                chartType="BarChart"
                                                legendToggle={false}
                                                data={retractionsPerJournalData()}
                                                options={retractionsPerJournalOptions}
                                            />
                                        }
                                    </Item>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Item elevation={4}>
                                        <Typography variant="caption" gutterBottom>Publishers with most retractions (Last {selectedTimeframe} Months)</Typography>
                                        {hasInitialData &&
                                            <Chart
                                                chartType="BarChart"
                                                data={retractionsPerPublisherData()}
                                                options={retractionsPerPublisherOptions}
                                            />
                                        }
                                    </Item>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6} height={'650px'} sx={{ mt: { xs: 4, md: 3 } }}>
                                    <Typography variant="body1" component="span">Biggest movers (Last 6 Months)</Typography>
                                    <Tooltip key="biggest_movers_journal_tooltip" placement="top" title={biggest_movers_explanation}>
                                        <InfoIcon color="primary" fontSize="small" style={{ verticalAlign: 'top' }} />
                                    </Tooltip>
                                    {hasInitialData &&
                                        <DataGrid
                                            rows={dashboardData?.biggest_movers_journals}
                                            columns={biggestMoversJournalsColumns}
                                            getRowId={getRowIdForJournal}
                                            rowCount={dashboardData?.biggest_movers_journals.length}
                                            pageSizeOptions={[10]}
                                            autoHeight={false}
                                            sx={{ display: 'grid' }}
                                        />
                                    }
                                </Grid>
                                <Grid item xs={12} md={6} height={'650px'} mb={10} sx={{ mt: { xs: 4, md: 3 } }}>
                                    <Typography variant="body1" component="span">Biggest movers (Last 6 Months)</Typography>
                                    <Tooltip key="biggest_movers_publishers_tooltip" placement="top" title={biggest_movers_explanation}>
                                        <InfoIcon color="primary" fontSize="small" style={{ verticalAlign: 'top' }} />
                                    </Tooltip>
                                    {hasInitialData &&
                                        <DataGrid
                                            rows={dashboardData?.biggest_movers_publishers}
                                            columns={biggestMoversPublisherColumns}
                                            getRowId={getRowIdForPublisher}
                                            rowCount={dashboardData?.biggest_movers_publishers.length}
                                            pageSizeOptions={[10]}
                                            autoHeight={false}
                                            sx={{ display: 'grid' }}
                                        />
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Item>
            </>
        }
    </Box>
}

export default RetractionControlCenter